<script setup>
  import { ref, computed, nextTick, onMounted } from 'vue';
  import { useUserStore } from '@/stores/UserStore';
  import { useCompleteReportStore } from '@/stores/CompleteReportStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import draggable from 'vuedraggable'
  import BaseModal from '@/components/base/BaseModal.vue';
  import ToggleItem from '@/components/controls/ToggleItem.vue';
  import SegmentedControl from '@/components/controls/SegmentedControl.vue';
  import SegmentedControlButton from '@/components/controls/SegmentedControlButton.vue';  
  import SettingsListItem from "@/components/controls/SettingsListItem.vue";
  import Toggle from "@/components/controls/Toggle.vue";  
  import Icon from "@/components/icons/Icon.vue";
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';

  const { $ENV } = useConfigurationGlobals();   

  const userStore = useUserStore();
  const completeReportStore = useCompleteReportStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const showSettingsModal = defineModel('showSettingsModal', { default: true });

  const mode = ref('use');

  // const draggable = computed(() => mode.value === 'arrange');
  const dragging = ref(false);
  const arrangeButtonText = computed(() => mode.value === 'arrange' ? 'Done' : 'Arrange');
  const allowReorder = computed(() => (item) => completeReportStore.components[item].allowReorder);
  const allowHidden = computed(() => (item) => completeReportStore.components[item].allowHidden);
  const lastUpdateTimeExample = computed(() => completeReportStore.lastUpdateTime === 'timestamp' ? '25 Oct 10:50 AM CDT' : '10 mins ago');
  const componentSubsettings = computed(() => (item) => {
    return completeReportStore.components[item].settings?.filter(setting => setting.show ? setting.show() : true);
  });
  const showSubsettings = computed(() => (item) => completeReportStore.activeContainers.indexOf(item) > -1 && completeReportStore.components[item]?.settings && componentSubsettings.value(item)?.length > 0 && mode.value === 'use' || false);
  const isDevelopment = computed(() => $ENV === 'development' || import.meta.env.DEV);

  function toggleMode() {
    mode.value = mode.value === 'arrange' ? 'use' : 'arrange';
  }

  function handleSortStart() {
    dragging.value = true;
  }

  function handleSortEnd(e) {
    dragging.value = false;

    if (e.oldIndex === e.newIndex) return;

    const containerId = completeReportStore.containerOrder[e.newIndex];
    const excludedIds = [1000, 1001, 1100];
    const newPosition = completeReportStore.containerOrder
      .filter(id => !excludedIds.includes(id))
      .indexOf(containerId) + 1;

    googleAnalyticsStore.sendEvent('Complete_Report_Container_Order_Change', {
      Type: completeReportStore.components[containerId].props.title,
      New_Position: newPosition,
    });
  }

  const handleUpdateTimeChange = (e) => {
    googleAnalyticsStore.sendEvent('Complete_Report_Update_Time_Format_Change', {
      Type: e.value.charAt(0).toUpperCase() + e.value.slice(1),
    });
  }

  const handleContainerToggle = async (element) => {
    await nextTick();

    const isShown = completeReportStore.activeContainers.includes(element);

    googleAnalyticsStore.sendEvent(isShown ? 'Complete_Report_Container_Show' : 'Complete_Report_Container_Hide', {
      Type: completeReportStore.components[element].props.title,
    });
  }

  const handleSettingChange = (element, setting, value) => {
    googleAnalyticsStore.sendEvent('Complete_Report_Container_Setting_Change', {
      Type: completeReportStore.components[element].props.title,
      Setting: setting.label,
      Value: value,
    });
  }

  onMounted(() => {
    googleAnalyticsStore.sendEvent('Complete_Report_Settings_Show');
  });
</script>

<template>
  <BaseModal
    v-model="showSettingsModal"
    :allowOutsideClick="false"
    height="tall"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">Complete Report Settings</h2>
    </header>          
    <div class="jwx-modal__body">
      <div class="jwx-modal__section">
        <p class="jwx-modal__text">Customize the complete report to your needs. Change what information is displayed, how it is displayed, and the order it is displayed in. These changes will be applied to the complete report for all spots.</p>
      </div>
      <div class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">Last Update Time</h3>
        </div>        
        <SegmentedControl
          v-model="completeReportStore.lastUpdateTime"
          @segmentChanged="handleUpdateTimeChange"
          defaultValue="timestamp"
          label="Last Update Time"
        >
          <SegmentedControlButton value="timestamp">Timestamp</SegmentedControlButton>
          <SegmentedControlButton value="relative">Relative</SegmentedControlButton>
        </SegmentedControl>
        <p class="jwx-modal__section-description"><span class="jwx-text-color-subtle">Example: </span>{{ lastUpdateTimeExample }}</p>
      </div>
      <div class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">Data Cards*</h3>
          <div class="jwx-modal__section-actions">
            <button
              v-if="completeReportStore.userChangedSettings"
              @click.prevent="completeReportStore.resetContainers"
              type="button" 
              class="jwx-button jwx-button--pill jwx-button--small jwx-button--ghost"
              data-variation="dark-text"
            >
              <Icon name="rotate-right" class="jwx-button__icon" />
              Reset
            </button>

            <button
              @click.prevent="toggleMode"
              type="button" 
              class="jwx-button jwx-button--pill jwx-button--small"
              :class="{ 'jwx-button--active': mode === 'arrange' }"
            >
              <Icon v-if="mode !== 'arrange'" name="up-down-arrows" class="jwx-button__icon" />
              {{ arrangeButtonText }}
            </button>
          </div>
        </div>

        <draggable 
          :list="completeReportStore.containerOrder" 
          :item-key="(item) => item"
          @start="handleSortStart" 
          @end="handleSortEnd"
          :disabled="mode === 'use'"
          class="jwx-settings"
          draggable=".jwx-settings__item"
          tag="ul"
          :animation="200"
        >
          <template #item="{element}">
            <SettingsListItem
              :key="element"
              :draggable="true"
              :dragMode="mode"
              :showSubsettings="showSubsettings(element)"
            >
              <template #default>
                <p class="jwx-settings__label">
                  {{ completeReportStore.components[element].props.title }}
                </p>
                <Toggle
                  v-if="allowHidden(element)"
                  :id="`container-${element}`"
                  class="jwx-settings__toggle"
                  :value="element"
                  :disabled="mode === 'arrange'"
                  v-model="completeReportStore.activeContainers"
                  @input:change="() => handleContainerToggle(element)"
                >
                </Toggle>
                <div v-else-if="!allowHidden(element)" class="jwx-settings__edit-only">
                  <p class="jwx-settings__label">{{ completeReportStore.components[element].props.title }}</p>
                </div>
              </template>
              <template #subsettings>
                <li 
                  v-for="setting in componentSubsettings(element)"
                  class="jwx-settings__subsettings-item"
                  :key="setting.id"
                >
                  <p class="jwx-settings__subsetting-label">
                    {{ setting.label }}
                  </p>
                  <SegmentedControl
                    v-if="setting.type === 'segmentedControl'"
                    v-model="setting.model"
                    @segmentChanged="(e) => handleSettingChange(element, setting, e.value)"
                    :defaultValue="setting.defaultValue"
                    :label="setting.label"
                  >
                    <SegmentedControlButton
                      v-for="option in setting.options"
                      :value="option.value"
                      :key="`${setting.id}-${option.value}`"
                    >
                      {{ option.label }}
                    </SegmentedControlButton>
                  </SegmentedControl>

                  <Toggle
                     v-if="setting.type === 'toggle'"
                    :id="`${element}-${setting.id}`"
                    class="jwx-settings__toggle"
                    :value="setting.defaultValue"
                    v-model="setting.model"
                    @input:change="(value) => handleSettingChange(element, setting, value)"
                  >
                  </Toggle>
                </li>
              </template>              
            </SettingsListItem>
          </template>
        </draggable>
        <p class="jwx-modal__section-description">* Data cards vary by spot</p>
      </div>
      <div
        v-if="completeReportStore.showCompleteReportSyncControl"
        class="jwx-modal__section"
      >    
        <ToggleItem
          :id="`sync-settings`"
          class="jwx-settings__toggle"
          v-model="completeReportStore.syncSettings"
        >
          Sync Settings
        </ToggleItem>
        <p class="jwx-modal__section-description">Turn Sync Settings on to sync between all browsers and devices associated with this account.</p>
      </div>
      <div
        v-if="isDevelopment"
        class="jwx-modal__section"
      >    
        <button 
          type="button" 
          @click.prevent="userStore.deleteAPIStorage"
          class="jwx-button jwx-button--danger"
        >
          Delete User Storage
        </button>
        <p class="jwx-modal__section-description">Use this to fix any issues caused by conflicts between development and production content stored with the user storage api.</p>
      </div>
    </div>
    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button 
          type="button" 
          @click.prevent="showSettingsModal = !showSettingsModal"
          class="jwx-button jwx-button--success"
        >
          Done
        </button>
      </div>
    </div>                  
  </BaseModal>  
</template>